import { AskAIButtonProps } from "@/components/static-dashboard-components/charts-components/ChartTopBar";
import { useSelectedChart } from "@/contexts/ChartsContext";
import { Colors } from "@/enums/Colors";
import { MainDashBoardChart } from "@/enums/MainDashBoardChart";
import { AverageChartData } from "@/models/charts/AverageChartData";
import { ChartBankAccount } from "@/models/charts/ChartBankAccount";
import { Box, Button, Divider, styled, Typography } from "@mui/material";
import { CollapseChart } from "./CollapseChart";

interface Props {
  data: AverageChartData<ChartBankAccount>;
}

const US_DOLLAR = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

export const TodayCash = ({ data, disableAskAIButton = false, disableExpand = false }: Props & AskAIButtonProps) => {
  const formattedNumber = `${data?.currentValue && data?.currentValue > 0 ? "+" : ""} ${US_DOLLAR.format(
    data?.currentValue ? data.currentValue : 0
  )}`;

  const currencyColor =
    data?.currentValue && data?.currentValue > 0 ? Colors.GREEN_01A902 : data?.currentValue == 0 ? Colors.BLACK_151517 : Colors.RED;

  const { selectedChart, setSelectedChart } = useSelectedChart();
  const onClickMissingAccounts = () => {};

  const renderCollapseContent = () => {
    return (
      <Container display="flex" flexDirection="column">
        <Typography className="description">{todaysCashDescription}</Typography>
        <Divider className="divider" />

        <Container className="bank-account-list">
          {data?.items?.map((ba) => (
            <Box display="flex" justifyContent="space-between" key={ba.name}>
              <Typography fontWeight={500} fontSize={"12px"}>
                {ba.name}
              </Typography>
              <Typography fontWeight={500} fontSize={"12px"}>
                {US_DOLLAR.format(ba.value)}
              </Typography>
            </Box>
          ))}
        </Container>

        <Button sx={{ textTransform: "none", placeSelf: "flex-end" }} onClick={() => onClickMissingAccounts}>
          <Typography className="missing-account-text">Missing any account?</Typography>
        </Button>
      </Container>
    );
  };

  return (
    <CollapseChart
      selected={selectedChart === MainDashBoardChart.TODAY_CASH}
      collapseContent={renderCollapseContent()}
      title="Today’s Cash"
      value={formattedNumber}
      colorValue={currencyColor}
      disableExpand={disableExpand}
      disableAskAIButton={disableAskAIButton}
      onClickStarIcon={() => {
        setSelectedChart(MainDashBoardChart.TODAY_CASH);
      }}
    />
  );
};

const Container = styled(Box)(() => ({
  "&.bank-account-list": {
    maxHeight: "5rem",
    paddingRight: "0.3125rem",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "0.3125rem",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "1rem",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
  },
  "& .missing-account-text": {
    paddingTop: "0.625rem",
    textAlign: "end",
    color: Colors.LAZO_PURPLE,
  },
  "& .divider": {
    color: Colors.LIGHT_GRAY_4a4b4e,
    marginBottom: "0.625rem",
    marginTop: "0.625rem",
  },
  "& .description": {
    color: Colors.LIGHT_GRAY_8A8E93,
    fontSize: "12px",
  },
}));

const todaysCashDescription =
  "Today's Cash balance is updated daily and may vary from the original source due to timing discrepancies in data synchronization. This figure represents cash, cash equivalents, and similar types of accounts, which are automatically imported from connected accounting software services and bank accounts.";
